import { Component } from '@theme/component';

/**
 * Master/detail switcher for the mega menu.
 *
 * Column 1 (`.mega-menu__master`) is an ARIA tablist of L2 categories; hovering or
 * focusing one reveals only its L3 children in column 2 (`.mega-menu__detail`, a
 * tabpanel) and hides the others. The first item is active by default (server-side).
 *
 * Keyboard (Phase B): roving tabindex with Arrow keys + Home/End (automatic
 * activation on focus), Escape returns focus to the top-level menu trigger.
 * The roving mechanism mirrors DropdownLocalizationComponent in localization.js.
 */
class MegaMenuMasterDetail extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.removeEventListener('keydown', this.#onKeydown);
  }

  /**
   * Activate the master item under the pointer or focus. Bound declaratively via
   * `on:pointerenter="/select"` and `on:focus="/select"` on each master tab.
   * @param {PointerEvent | FocusEvent} event
   */
  select = (event) => {
    const trigger = /** @type {Element} */ (event.target).closest('[data-index]');
    if (trigger instanceof HTMLElement) this.#activate(trigger.dataset.index);
  };

  /** @returns {HTMLElement[]} The column 1 tabs, in DOM order. */
  get #tabs() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('.mega-menu__master-link')]);
  }

  /**
   * Roving keyboard navigation for column 1. Mirrors localization.js
   * (#onContainerKeyDown / #changeCountryFocus): move focus, and focusing a tab
   * auto-activates its panel via the `on:focus="/select"` binding.
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        event.preventDefault();
        this.#moveFocus(1);
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        event.preventDefault();
        this.#moveFocus(-1);
        break;
      case 'Home':
        event.preventDefault();
        this.#tabs[0]?.focus();
        break;
      case 'End':
        event.preventDefault();
        this.#tabs.at(-1)?.focus();
        break;
      case 'Escape':
        this.#close();
        break;
    }
  };

  /**
   * Move focus between tabs with wraparound.
   * @param {number} step - +1 for next, -1 for previous.
   */
  #moveFocus(step) {
    const tabs = this.#tabs;
    if (tabs.length === 0) return;
    const current = tabs.findIndex((tab) => tab === document.activeElement);
    const next = current < 0 ? 0 : (current + step + tabs.length) % tabs.length;
    tabs[next]?.focus();
  }

  /** Escape: return focus to the controlling top-level menu link (WCAG focus management). */
  #close() {
    const trigger = this.closest('.menu-list__list-item')?.querySelector(':scope > .menu-list__link');
    if (trigger instanceof HTMLElement) trigger.focus();
  }

  /**
   * @param {string | undefined} index
   */
  #activate(index) {
    if (index == null) return;

    for (const tab of this.#tabs) {
      const active = tab.getAttribute('data-index') === index;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
    }

    for (const panel of this.querySelectorAll('.mega-menu__detail-panel')) {
      const active = panel.getAttribute('data-index') === index;
      panel.classList.toggle('is-active', active);
      panel.toggleAttribute('hidden', !active);
    }
  }
}

if (!customElements.get('mega-menu-master-detail')) {
  customElements.define('mega-menu-master-detail', MegaMenuMasterDetail);
}
